import {
  selectAutoscalingEnabledById,
  selectScaleRecommendationStateByServiceId,
  selectServiceById,
  selectServiceSizingPolicyById,
} from "@app/deploy";
import {
  getCpuLimit,
  getInstanceProfileLabel,
} from "@app/deploy/container/utils";
import {
  getDatabaseLabel,
  getDatabaseMultiplier,
} from "@app/deploy/database/utils";
import { selectOrganizationSelected } from "@app/organizations";
import { useSelector } from "@app/react";
import {
  appServicePathMetricsUrl,
  appServiceScalePathUrl,
  databaseMetricsUrl,
  databaseScaleUrl,
} from "@app/routes";
import { schema } from "@app/schema";
import { capitalize } from "@app/string-utils";
import { tunaEvent } from "@app/tuna";
import type {
  DeployService,
  DeployServiceSizingPolicy,
  InstanceClass,
  ManualScaleRecommendation,
} from "@app/types";
import cn from "classnames";
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Box } from "./box";
import { Button, ButtonLink, ButtonLinkDocs } from "./button";
import { Disclosure } from "./disclosure";
import { Group } from "./group";
import { IconInfo, IconScaleCheck, IconScaleDown, IconScaleUp } from "./icons";
import { Pill } from "./pill";
import { Tooltip, type TooltipProps } from "./tooltip";

// We now compute validity/direction via deploy selector; keep only
// lightweight helpers here for message copy

interface ScaleDirectionPillProps {
  className?: string;
}

export const ScaleDownStatus = ({ className }: ScaleDirectionPillProps) => {
  return (
    <Pill
      className={cn("w-max", className)}
      variant="progress"
      icon={<IconScaleDown variant="sm" />}
    >
      Over-provisioned
    </Pill>
  );
};

export const ScaleUpStatus = ({ className }: ScaleDirectionPillProps) => {
  return (
    <Pill
      className={cn("w-max", className)}
      variant="error"
      icon={<IconScaleUp variant="sm" />}
    >
      Under-provisioned
    </Pill>
  );
};

export const RightSizedStatus = ({ className }: ScaleDirectionPillProps) => {
  return (
    <Pill
      className={cn("w-max", className)}
      icon={<IconScaleCheck variant="sm" />}
    >
      Right-sized
    </Pill>
  );
};

export const AutoscalingOnStatus = ({ className }: ScaleDirectionPillProps) => {
  return (
    <Pill
      className={cn("w-max", className)}
      variant="success"
      icon={<IconScaleCheck variant="sm" />}
    >
      Autoscaling ON
    </Pill>
  );
};

const formatPercent = (value: number): string =>
  value > 1 ? value.toFixed(1) : "< 1";

const getAutoscalingTooltipData = (
  policy: DeployServiceSizingPolicy,
  scaleUrl: string,
): Omit<TooltipProps, "children"> => {
  const isHorizontal = policy.autoscaling === "horizontal";
  const header = (
    <>
      {capitalize(policy.autoscaling)} Autoscaling:{" "}
      <span className="text-lime">ON</span>
    </>
  );

  const values = isHorizontal
    ? [
        { label: "Min. Containers:", value: policy.minContainers ?? "" },
        { label: "Max Containers:", value: policy.maxContainers ?? "" },
        {
          label: "Scale Down CPU Threshold:",
          value: policy.minCpuThreshold ?? "",
        },
        {
          label: "Scale Up CPU Threshold:",
          value: policy.maxCpuThreshold ?? "",
        },
      ]
    : [
        { label: "Minimum Memory:", value: policy.minimumMemory },
        {
          label: "Maximum Memory:",
          value: policy.maximumMemory ?? "Not set",
        },
      ];

  return {
    header,
    values,
    link: { label: "View settings", to: scaleUrl },
  };
};

const formatLastEvaluatedDate = (createdAt: string): string => {
  const d = new Date(createdAt);
  const dateStr = d.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  });
  const timeStr = d.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
    timeZone: "UTC",
  });
  return `${dateStr} at ${timeStr} UTC`;
};

const getScaleRecDescriptionParts = (
  recommendation: ManualScaleRecommendation,
  service: DeployService,
  state: "scale_up" | "scale_down",
) => {
  const newContainerSize =
    recommendation.recommendedContainerMemoryLimitMb / 1024;
  const profileName = getInstanceProfileLabel(
    recommendation.recommendedInstanceClass as InstanceClass,
  );

  const recommendationLine =
    state === "scale_up"
      ? `Scale up to a ${newContainerSize} GB container using the ${profileName} profile to improve reliability and performance.`
      : `Scale down to a ${newContainerSize} GB container using the ${profileName} profile.`;

  const cpuLimit = getCpuLimit(service);
  const cpuPercent = formatPercent(recommendation.cpuUsage * 100);
  const cpuBullet = `${cpuPercent}% / ${cpuLimit}%`;

  const memoryPercent = formatPercent(
    (recommendation.ramUsage / recommendation.containerMemoryLimitMbAtTime) *
      100,
  );
  const memoryUsageGb = (recommendation.ramUsage / 1024).toFixed(1);
  const memoryLimitGb = (
    recommendation.containerMemoryLimitMbAtTime / 1024
  ).toFixed(1);
  const memoryBullet = `${memoryPercent}% / 100% (${memoryUsageGb} GB / ${memoryLimitGb} GB)`;

  return {
    recommendationLine,
    cpuBullet,
    memoryBullet,
  };
};

const getScaleRecTooltipData = (
  recommendation: ManualScaleRecommendation,
  service: DeployService,
  state: "scale_up" | "scale_down",
  scaleUrl: string,
): Omit<TooltipProps, "children"> => {
  const { cpuBullet, memoryBullet } = getScaleRecDescriptionParts(
    recommendation,
    service,
    state,
  );
  const formattedDate = formatLastEvaluatedDate(recommendation.createdAt);
  const isDatabase = service.databaseId !== "";
  const overheadMultiplier =
    isDatabase && getDatabaseMultiplier(service.processType);
  const dbDisplayName = isDatabase && getDatabaseLabel(service.processType);
  const multiplierNoteContent =
    overheadMultiplier && dbDisplayName
      ? ` Note: For safety and reliability, we apply a ${overheadMultiplier}x multiplier to memory recommendations for ${dbDisplayName} databases.`
      : "";
  const description = `Last evaluated on ${formattedDate}, based on prior 14 days.${multiplierNoteContent}`;
  const statusLabel =
    state === "scale_up" ? "Under-Provisioned" : "Over-Provisioned";
  const statusColor = state === "scale_up" ? "text-red-300" : "text-indigo";

  return {
    header: (
      <>
        Scale: <span className={statusColor}>{statusLabel}</span>
      </>
    ),
    values: [
      { label: "CPU Usage (p95):", value: cpuBullet },
      { label: "RAM Usage (peak):", value: memoryBullet },
    ],
    description,
    link: { label: "Scale", to: scaleUrl },
  };
};

export const formatSavingsCurrency = (num?: number): string =>
  Math.round(num || 0).toLocaleString("en", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });

const formatEstSavings = (costSavings: number): string =>
  `Save up to ${formatSavingsCurrency(costSavings)}/mo.`;

const RECOMMENDATION_DISCLAIMER =
  "Recommendations update once daily and may not reflect the most recent usage changes.";

function getScaleRecommendationHeadingTooltip(service: DeployService): string {
  const isDatabase = service.databaseId !== "";
  const overheadMultiplier =
    isDatabase && getDatabaseMultiplier(service.processType);
  const dbDisplayName = isDatabase && getDatabaseLabel(service.processType);
  const multiplierNote =
    overheadMultiplier && dbDisplayName
      ? ` Note that, for safety and reliability, we apply a ${overheadMultiplier}x multiplier to memory recommendations for ${dbDisplayName} databases.`
      : "";
  return `${RECOMMENDATION_DISCLAIMER}${multiplierNote}`;
}

const ScaleRecommendationDescription = ({
  recommendation,
  service,
  state,
  tooltipData,
}: ScaleRecommendationMessageProps & {
  state: "scale_up" | "scale_down";
  tooltipData: Omit<TooltipProps, "children">;
}) => {
  const { recommendationLine } = getScaleRecDescriptionParts(
    recommendation,
    service,
    state,
  );
  return (
    <div className="mb-2">
      <div className="flex items-center gap-1.5 text-md font-semibold text-gray-900">
        <span>Scaling Recommendation</span>
        <Tooltip text={getScaleRecommendationHeadingTooltip(service)}>
          <span className="inline-flex cursor-help">
            <IconInfo variant="sm" className="opacity-50 hover:opacity-100" />
          </span>
        </Tooltip>
      </div>
      <div className="text-black-500">
        {recommendationLine}{" "}
        <Tooltip {...tooltipData} className="inline-block align-baseline">
          <span className="cursor-help text-gray-500 hover:text-gray-700 border-b border-dotted border-gray-400">
            See usage stats
          </span>
        </Tooltip>
      </div>
    </div>
  );
};

interface ScaleRecommendationMessageProps {
  recommendation: ManualScaleRecommendation;
  service: DeployService;
}

const CONTAINER_EFFICIENCY_DOCS_URL =
  "https://aptible.com/docs/core-concepts/scaling/container-right-sizing-recommendations";

export interface RecommendationBlockProps {
  title: string;
  pill: React.ReactNode;
  description?: React.ReactNode;
  pillTooltipProps?: Omit<TooltipProps, "children">;
  expanded: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}

export const RecommendationBlock = ({
  title,
  pill,
  description,
  pillTooltipProps,
  expanded,
  onToggle,
  children,
}: RecommendationBlockProps) => {
  const pillNode = (
    <div className="inline-block">
      {pillTooltipProps ? (
        <Tooltip {...pillTooltipProps}>
          <span className="cursor-help">{pill}</span>
        </Tooltip>
      ) : (
        pill
      )}
    </div>
  );

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between gap-4">
        <div className="flex flex-col gap-1">
          <span className="text-md font-semibold text-gray-900">{title}</span>
          <div className="flex flex-col items-start gap-0.5">
            {pillNode}
            {description ?? null}
          </div>
        </div>
        <Disclosure
          labelWhenClosed="See recommendation"
          labelWhenOpen="Hide recommendation"
          open={expanded}
          onToggle={onToggle}
        />
      </div>
      {expanded ? <div className="flex flex-col gap-3">{children}</div> : null}
    </div>
  );
};

export const InsightsAndRecommendationsSection = ({
  children,
}: { children: React.ReactNode }) => (
  <Box>
    <div className="flex flex-col gap-4">
      <h1 className="text-lg text-gray-500">Insights and Recommendations</h1>
      {children}
    </div>
  </Box>
);

const SESSION_STORAGE_KEY = (id: string) => `containerEfficiencyExpanded-${id}`;

function getStoredExpanded(serviceId: string): boolean {
  if (typeof sessionStorage === "undefined") return true;
  try {
    const v = sessionStorage.getItem(SESSION_STORAGE_KEY(serviceId));
    return v !== "false";
  } catch {
    return true;
  }
}

function setStoredExpanded(serviceId: string, expanded: boolean): void {
  try {
    sessionStorage.setItem(SESSION_STORAGE_KEY(serviceId), String(expanded));
  } catch {
    // ignore
  }
}

export const ContainerEfficiencySection = ({
  serviceId,
  onAcceptRecommendation,
}: { serviceId: string; onAcceptRecommendation: () => void }) => {
  const [expanded, setExpanded] = useState(() => getStoredExpanded(serviceId));

  useEffect(() => {
    setExpanded(getStoredExpanded(serviceId));
  }, [serviceId]);

  const service = useSelector((s) => selectServiceById(s, { id: serviceId }));
  const recommendationState = useSelector((s) =>
    selectScaleRecommendationStateByServiceId(s, { id: serviceId }),
  );
  const recommendation = useSelector(
    (s) =>
      schema.manualScaleRecommendations
        .selectTableAsList(s)
        .find((r) => r.serviceId === serviceId) ||
      schema.manualScaleRecommendations.empty,
  );

  if (recommendationState === "none") {
    return null;
  }

  const showSaveAmount =
    recommendationState === "scale_down" && recommendation.costSavings > 0;
  const scaleUrl = service.appId
    ? appServiceScalePathUrl(service.appId, serviceId)
    : databaseScaleUrl(service.databaseId);
  const tooltipData = getScaleRecTooltipData(
    recommendation,
    service,
    recommendationState,
    scaleUrl,
  );

  return (
    <InsightsAndRecommendationsSection>
      <RecommendationBlock
        title="Container Efficiency"
        pill={
          recommendationState === "scale_up" ? (
            <ScaleUpStatus />
          ) : (
            <ScaleDownStatus />
          )
        }
        description={
          showSaveAmount ? (
            <span className="text-green-400 text-xs">
              {formatEstSavings(recommendation.costSavings)}
            </span>
          ) : undefined
        }
        pillTooltipProps={tooltipData}
        expanded={expanded}
        onToggle={() => {
          setExpanded((e) => {
            const next = !e;
            setStoredExpanded(serviceId, next);
            return next;
          });
        }}
      >
        <ScaleRecommendationDescription
          recommendation={recommendation}
          service={service}
          state={recommendationState}
          tooltipData={tooltipData}
        />
        <div className="flex items-center justify-between gap-4 border-t border-black-100 pt-3">
          <Group variant="horizontal" className="items-center gap-2">
            <Button onClick={onAcceptRecommendation} size="sm">
              Autofill Changes
            </Button>
            <ButtonLink
              to={
                service.appId
                  ? appServicePathMetricsUrl(service.appId, serviceId)
                  : databaseMetricsUrl(service.databaseId)
              }
              variant="white"
              size="sm"
            >
              View Metrics
            </ButtonLink>
          </Group>
          <ButtonLinkDocs href={CONTAINER_EFFICIENCY_DOCS_URL} />
        </div>
      </RecommendationBlock>
    </InsightsAndRecommendationsSection>
  );
};

export const ManualScaleRecView = ({ serviceId }: { serviceId: string }) => {
  const service = useSelector((s) => selectServiceById(s, { id: serviceId }));
  const org = useSelector(selectOrganizationSelected);
  const recommendationState = useSelector((s) =>
    selectScaleRecommendationStateByServiceId(s, { id: serviceId }),
  );
  const recommendation = useSelector(
    (s) =>
      schema.manualScaleRecommendations
        .selectTableAsList(s)
        .find((r) => r.serviceId === serviceId) ||
      schema.manualScaleRecommendations.empty,
  );
  const navigate = useNavigate();

  if (recommendationState === "none") {
    return (
      <Tooltip
        header={
          <>
            Rightsized: <span className="text-lime">True</span>
          </>
        }
        description="Based on the last 14 days of metrics, updated once daily."
      >
        <span className="cursor-help">
          <RightSizedStatus />
        </span>
      </Tooltip>
    );
  }

  const url = service.appId
    ? appServiceScalePathUrl(service.appId, serviceId)
    : databaseScaleUrl(service.databaseId);

  const scaleDir = recommendationState === "scale_up" ? "up" : "down";

  const onScale = () => {
    navigate(url);
    tunaEvent(
      `scale-service-click-${scaleDir}`,
      JSON.stringify({
        orgId: org.id,
        orgName: org.name,
        serviceId,
        direction: scaleDir,
      }),
    );
  };

  const pillTooltipData = getScaleRecTooltipData(
    recommendation,
    service,
    recommendationState,
    url,
  );

  return (
    <div className="flex flex-col items-start gap-0.5">
      <Tooltip {...pillTooltipData}>
        <button type="button" onClick={onScale} className="cursor-pointer">
          {recommendationState === "scale_up" ? (
            <ScaleUpStatus />
          ) : (
            <ScaleDownStatus />
          )}
        </button>
      </Tooltip>
      {recommendation.costSavings > 0 && (
        <div className="text-green-400 text-xs">
          {formatEstSavings(recommendation.costSavings)}
        </div>
      )}
    </div>
  );
};

export const ScaleRecsView = ({ service }: { service: DeployService }) => {
  const enabled = useSelector((s) =>
    selectAutoscalingEnabledById(s, { id: service.serviceSizingPolicyId }),
  );
  const policy = useSelector((s) =>
    selectServiceSizingPolicyById(s, { id: service.serviceSizingPolicyId }),
  );
  const scaleUrl = service.appId
    ? appServiceScalePathUrl(service.appId, service.id)
    : databaseScaleUrl(service.databaseId);

  return (
    <div>
      {enabled ? (
        <Tooltip {...getAutoscalingTooltipData(policy, scaleUrl)}>
          <Link
            to={scaleUrl}
            className="inline-block no-underline hover:no-underline"
          >
            <AutoscalingOnStatus className="cursor-pointer" />
          </Link>
        </Tooltip>
      ) : null}
      {enabled ? null : <ManualScaleRecView serviceId={service.id} />}
    </div>
  );
};
